/*
 * allcities-search.js — lazy-loaded city/variant search.
 *
 * The search list (~thousands of <li> + <img>) used to be inlined into every
 * page's HTML 3-4x via header-search/mega-menu, bloating the document to
 * ~3.5MB and freezing the main thread for seconds building the DOM. Now the
 * list HTML is fetched on demand (first focus of a .citySearchInput) from a
 * layout-less product view (templates/product.search-index.liquid) and
 * injected into the nearest `.allcities-mount`. This file holds the search
 * behaviour and is the only thing loaded eagerly (small, cached); it does
 * nothing until the user focuses a search box.
 */
(function () {
  if (window.__allCitiesSearchInit) return;
  window.__allCitiesSearchInit = true;

  // ---- Fuse.js (CDN) loaded lazily, once, on first search use ----
  var _fusePromise = null;
  function loadFuse() {
    if (window.Fuse) return Promise.resolve();
    if (_fusePromise) return _fusePromise;
    _fusePromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/fuse.js@6.6.2';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return _fusePromise;
  }

  // ---- Search behaviour (ported verbatim from the old inline snippet) ----
  function initAllCitiesSearch(root, payload) {
    if (!root || !payload || !payload.items) return;

  const CARD_THRESHOLD = 30;

  // Inputs stay global so every search box (desktop mega-menu + mobile
  // overlay) drives this instance's list.
  const inputs = document.querySelectorAll('.citySearchInput');

  // Helper: remove flag emojis + lowercase + trim + strip diacritics.
  // Separators (hyphens, en/em dashes, slashes) and any whitespace run are
  // collapsed to a single space so a variant token like "dubai - marina"
  // and a query like "dubai marina" (or "dubai-marina") normalize to the
  // same "dubai marina" — both for Fuse matching and the substring guard.
  const stripFlags = s => s?.replace(/[\u{1F1E6}-\u{1F1FF}]{2}/gu, "") ?? "";
  const stripDiacritics = s => s.normalize("NFD").replace(/\p{Diacritic}/gu, "");
  const normalizeText = s => stripDiacritics(stripFlags(String(s)))
    .toLowerCase()
    .replace(/[\s\u2010-\u2015\/-]+/g, " ")
    .trim();

  /* Aliases / synonyms now live in Shopify metaobject `related_terms` fields,
     delivered via the JSON payload — cities in payload.rel, countries in
     payload.crel. The old hardcoded cityAliases / countryAliases maps were
     removed: they were English-keyed (so they only ever worked in the EN
     store) and are fully superseded by the metafields, which work in every
     locale. normalizeText/stripFlags/stripDiacritics above are still used. */

  // Related-terms lookup (payload.rel): city display-name → extra tokens
  // (english_name + native local_name + the related_terms metafield). Re-key
  // by normalized name and normalize the values so they line up with the tokens
  // we match against. Cities use it by their own name; variants inherit it via
  // their city part (d.ck) so "Tokyo" terms also apply to the Tokyo CUBE/FRAME.
  const relmap = {};
  {
    const relRaw = payload.rel || {};
    for (const k in relRaw) {
      const nk = normalizeText(k);
      if (!nk) continue;
      const vals = (relRaw[k] || []).map(normalizeText).filter(Boolean);
      relmap[nk] = (relmap[nk] || []).concat(vals);
    }
  }

  // Country-terms lookup (payload.crel): country name → exonyms, same shape as
  // relmap. Applied to a city's country tokens for cross-language country
  // search ("Deutschland"/"Allemagne" → German cities in any store).
  const crelmap = {};
  {
    const crelRaw = payload.crel || {};
    for (const k in crelRaw) {
      const nk = normalizeText(k);
      if (!nk) continue;
      const vals = (crelRaw[k] || []).map(normalizeText).filter(Boolean);
      crelmap[nk] = (crelmap[nk] || []).concat(vals);
    }
  }

  // Build search records from the JSON payload (no DOM needed). Each record
  // keeps its source data object (`d`) + its original index (`idx`) so we can
  // render the matched items on demand and preserve Liquid order. Tokens come
  // from the item's own name/title, the legacy alias map, AND the city's
  // related-terms bag (by city part for variants).
  const records = payload.items.map(function (d, idx) {
    const cityCanon = normalizeText(d.dc);         // city: name; variant: full title
    const cityKey = normalizeText(d.ck || d.dc);   // variant: city part; city: name
    const countryCanon = normalizeText(d.dco);

    const cityTokens = new Set([cityCanon]);
    if (cityKey) cityTokens.add(cityKey);
    (relmap[cityKey] || []).forEach(function (t) { cityTokens.add(t); });

    const countryTokens = new Set([countryCanon, ...(crelmap[countryCanon] || [])]);

    return {
      idx: idx,
      d: d,
      city_all: Array.from(cityTokens).filter(Boolean),
      country_all: Array.from(countryTokens).filter(Boolean)
    };
  });

  // The Custom Location CUBE, flagged with `cl` in the payload. It's a normal
  // searchable CUBE record, and doubles as the fallback shown when a query
  // matches nothing — so a search for a city we don't model yet (e.g. "Leipzig")
  // offers to build it instead of dead-ending on an empty list.
  const clRecord = records.find(function (r) { return r.d.cl; }) || null;

  const textUl = root.querySelector('.acs-text');
  const cardsBox = root.querySelector('.acs-cards');

  // Pre-build the lightweight all-cities name list ONCE (the empty state and
  // text mode). Names only — no images, no card markup — so it's cheap even
  // for thousands of cities, and filtering is just a class toggle. Card markup
  // (with images) is built on demand for the ≤30 matches of a narrowed query,
  // so we never parse thousands of <img>.
  const textByIdx = {};
  {
    let html = '';
    for (let i = 0; i < records.length; i++) {
      const r = records[i];
      if (r.d.s !== 'cities') continue;
      html += '<li class="acs-text-item" data-idx="' + r.idx + '">'
            + '<a href="' + r.d.u + '"' + (r.d.f ? ' class="acs-fav"' : '') + '>'
            + esc(r.d.n) + '</a></li>';
    }
    textUl.innerHTML = html;
    textUl.querySelectorAll('.acs-text-item').forEach(function (li) {
      textByIdx[li.getAttribute('data-idx')] = li;
    });
  }

  const fuse = new Fuse(records, {
    keys: [
      { name: "city_all", weight: 0.7 },
      { name: "country_all", weight: 0.3 }
    ],
    threshold: 0.2,
    ignoreLocation: false,
    distance: 100,
    minMatchCharLength: 3,
    includeScore: true
  });

  // For semi-fuzzy results, verify the query actually appears as a substring
  // in the city's token data. This prevents false positives like "china" → "chicago"
  // while preserving near-exact alias hits (score < 0.1) unconditionally.
  function isFalsePositive(result, q) {
    if (q.length < 4 || result.score < 0.1) return false;
    return ![...result.item.city_all, ...result.item.country_all].some(t => t.includes(q));
  }

  // Escape text destined for an innerHTML string (city/variant names, etc.).
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Card grouping order + labels. Brand names (CUBE/FRAME/GRAND/BLOK/CITYWALL)
  // are literal; the cities label and the descriptive section labels are
  // translated and passed through in payload.hc / payload.hl.
  const SECTION_ORDER = ['cities', 'cube', 'frame', 'grand', 'blok', 'citywall', 'cover', 'plaque', 'note', 'config'];
  const hl = payload.hl || {};
  const SECTION_LABELS = {
    cities: payload.hc || 'Cities',
    cube: 'CUBE', frame: 'FRAME', grand: 'GRAND', blok: 'BLOK',
    citywall: 'CITYWALL',
    cover: hl.cover || 'Display Cover',
    plaque: hl.plaque || 'Engraved Plaque',
    note: hl.note || 'Card with Note',
    config: hl.config || 'Configurators'
  };

  // Clearance rows (`sl` in the payload) get the localized Clearance Sale chip,
  // reusing the product cards' own .badge/.badge--cube classes so it matches
  // them exactly. The label ships in the payload so it stays translated.
  const SALE_LABEL = payload.hsl || '';
  function saleBadge(d) {
    if (!d.sl || !SALE_LABEL) return '';
    return '<span class="badge badge--cube acs-badge">' + esc(SALE_LABEL) + '</span>';
  }

  function cardHtml(d) {
    let info;
    if (d.s === 'cities') {
      const loc = d.loc ? ' <span class="acs-local">(' + esc(d.loc) + ')</span>' : '';
      info = '<span class="acs-country">' + esc(d.c || '') + '</span>'
           + '<span class="h6 acs-name' + (d.f ? ' acs-name--featured' : '') + '">' + esc(d.n) + loc + '</span>'
           + saleBadge(d);
    } else {
      const sub = d.sub ? '<span class="acs-sub">' + esc(d.sub) + '</span>' : '';
      info = '<span class="h6 acs-name acs-name--variant">' + esc(d.n) + sub + '</span>'
           + saleBadge(d);
    }
    // Thumbnail only when the item has an image; items without one (e.g. the
    // configurators) render text-only with no empty 60px gap.
    const thumb = d.i
      ? '<span class="acs-thumb-wrap"><img src="' + d.i + '" width="60" height="60" loading="lazy" decoding="async" class="acs-thumb" alt=""></span>'
      : '';
    return '<a href="' + d.u + '" class="acs-card">'
         + thumb
         + '<span class="acs-card-info">' + info + '</span></a>';
  }

  // Build card tiles for the matched records, grouped by section. Items keep
  // their original Liquid order within a section (sort by idx), not Fuse score
  // order — matches the previous behaviour. Empty sections are simply skipped,
  // so a query that only hits CUBE never shows a dangling FRAME/GRAND/BLOK
  // heading.
  function renderCards(results) {
    const bySection = {};
    results.forEach(function (r) {
      (bySection[r.item.d.s] = bySection[r.item.d.s] || []).push(r.item);
    });
    let html = '';
    SECTION_ORDER.forEach(function (sec) {
      const arr = bySection[sec];
      if (!arr || !arr.length) return;
      arr.sort(function (a, b) { return a.idx - b.idx; });
      html += '<div class="acs-heading h5' + (sec !== 'cities' ? ' acs-heading--divider' : '') + '">'
            + esc(SECTION_LABELS[sec]) + '</div>';
      arr.forEach(function (rec) { html += cardHtml(rec.d); });
    });
    cardsBox.innerHTML = html;
  }

  function apply(q, results, sectionFilter) {
    // No query → empty state: show every city name, no cards.
    if (!q) {
      root.classList.remove('acs--cards');
      cardsBox.innerHTML = '';
      for (const k in textByIdx) textByIdx[k].classList.remove('acs-text-item--hidden');
      return;
    }

    let filtered = results.filter(function (r) { return !isFalsePositive(r, q); });
    // Section-scoped query (e.g. "New York CUBE") → keep only that product
    // section, then always show it as cards (it's a deliberate product pick).
    if (sectionFilter) filtered = filtered.filter(function (r) { return r.item.d.s === sectionFilter; });

    // Nothing matched — a city we don't model yet, a typo, or a product-scoped
    // query with no hits. Offer the Custom Location CUBE instead of showing an
    // empty list, so the search is never a dead end.
    if (!filtered.length && clRecord) {
      root.classList.add('acs--cards');
      renderCards([{ item: clRecord }]);
      return;
    }

    const cardMode = sectionFilter
      ? filtered.length > 0
      : (filtered.length > 0 && filtered.length <= CARD_THRESHOLD);

    if (cardMode) {
      root.classList.add('acs--cards');
      renderCards(filtered);
      return;
    }

    // Broad match (or none) → text mode: matched city names only (variants
    // stay hidden), via class toggle on the pre-built list.
    root.classList.remove('acs--cards');
    cardsBox.innerHTML = '';
    const keep = {};
    filtered.forEach(function (r) { if (r.item.d.s === 'cities') keep[r.item.idx] = 1; });
    for (const k in textByIdx) textByIdx[k].classList.toggle('acs-text-item--hidden', !keep[k]);
  }

  // Product-type words that scope a query to a section. Brand names, so the
  // same in every language. "<city> <type>" (any order) filters to that
  // section instead of trying to match the combined phrase as one string.
  const TYPE_KEYWORDS = { cube: 'cube', frame: 'frame', grand: 'grand', blok: 'blok', citywall: 'citywall' };

  inputs.forEach(input => {
    input.addEventListener("input", function () {
      const raw = normalizeText(this.value);

      // Pull out a product-type word, if present, and search the remainder as
      // the city. Only scope when a usable (≥3 char) city remainder is left —
      // a bare "cube"/"frame" falls through to the normal path (empty state).
      let sectionFilter = null;
      const rest = [];
      raw.split(' ').forEach(function (w) {
        if (!sectionFilter && TYPE_KEYWORDS[w]) sectionFilter = TYPE_KEYWORDS[w];
        else rest.push(w);
      });
      const cleaned = rest.join(' ').trim();

      let q, sf;
      if (sectionFilter && cleaned.length >= 3) {
        q = cleaned; sf = sectionFilter;
      } else {
        // Treat 1–2 char queries as no query yet — keeps the full empty-state
        // list on screen until the user commits to a 3-character query.
        q = raw.length >= 3 ? raw : "";
        sf = null;
      }

      const results = q ? fuse.search(q) : [];
      apply(q, results, sf);
    });
  });

  }

  // ---- Lazy loader: fetch the list HTML on first search focus ----
  var _htmlPromise = null;
  function fetchListHTML(src) {
    if (_htmlPromise) return _htmlPromise;
    _htmlPromise = fetch(src, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('allcities-search HTTP ' + r.status);
      return r.text();
    });
    return _htmlPromise;
  }

  // Nearest `.allcities-mount` to the focused input (tightest ancestor first).
  function findMountFor(input) {
    var el = input;
    while (el && el !== document.body) {
      var m = el.querySelector('.allcities-mount');
      if (m) return m;
      el = el.parentElement;
    }
    return document.querySelector('.allcities-mount');
  }

  function loadMount(mount) {
    if (!mount || mount.dataset.acsLoaded) return;
    var src = mount.getAttribute('data-allcities-src');
    if (!src) return;
    mount.dataset.acsLoaded = '1';
    Promise.all([fetchListHTML(src), loadFuse()]).then(function (res) {
      // The fetched view is now tiny: a <style> block, an empty results
      // container, and a <script type="application/json"> data payload. The
      // big DOM (name list + card tiles) is built by initAllCitiesSearch, so
      // this innerHTML assignment no longer parses thousands of nodes/images.
      mount.innerHTML = res[0];
      var root = mount.querySelector('.acs-root');
      var dataEl = mount.querySelector('.acs-data');
      if (root && dataEl) {
        var payload = null;
        try { payload = JSON.parse(dataEl.textContent); }
        catch (e) { if (window.console) console.warn('allcities-search: bad JSON', e); }
        if (payload) initAllCitiesSearch(root, payload);
      }
    }).catch(function (err) {
      mount.dataset.acsLoaded = '';   // allow a retry on the next focus
      if (window.console) console.warn('allcities-search: load failed', err);
    });
  }

  // Load on focus (covers desktop click-into-input and keyboard users)…
  document.addEventListener('focusin', function (e) {
    var input = e.target.closest && e.target.closest('.citySearchInput');
    if (!input) return;
    loadMount(findMountFor(input));
  });

  // …and load as soon as a search box becomes VISIBLE — i.e. the moment the
  // search panel opens — so the list is there to browse without having to
  // tap the field first. On mobile, opening the panel doesn't auto-focus the
  // input, so focus alone left the list empty until the user tapped it. The
  // input sits at the top of the panel, so it's visible immediately on open
  // (no scrolling needed). Inputs are display:none while the panel is closed,
  // so this never fires at page load.
  function observeSearchInputs() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          loadMount(findMountFor(entry.target));
          io.unobserve(entry.target);
        }
      });
    });
    document.querySelectorAll('.citySearchInput').forEach(function (inp) { io.observe(inp); });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeSearchInputs);
  } else {
    observeSearchInputs();
  }

  // Fully prepare search in the background once the page is idle: fetch the
  // list HTML, load Fuse, AND inject the DOM + build the Fuse index for every
  // mount. Previously we only warmed the network (fetch + Fuse) and left the
  // expensive work — innerHTML parse of ~thousands of nodes + index build —
  // to run synchronously on first focus, which blocked the main thread for
  // 1–2s the moment the user opened search. Doing it here removes that stall:
  // by open time the list and index already exist, so it injects instantly.
  //
  // Safe to build early because the search panels are display:none while
  // closed — injecting a detached/hidden subtree costs no layout or paint,
  // and the `loading="lazy"` thumbnails don't fetch until the panel is shown.
  // Running via requestIdleCallback AFTER `load` means it never competes with
  // initial page render. Each mount builds in its own idle slot so a single
  // long task can't jank. loadMount() flips `acsLoaded` synchronously, so if
  // the user opens search before this finishes, the focus/intersection paths
  // become no-ops and the in-flight build still completes — no double work.
  function prefetchSearch() {
    var mounts = document.querySelectorAll('.allcities-mount');
    if (!mounts.length) return;
    loadFuse();
    mounts.forEach(function (mount, i) {
      var build = function () { loadMount(mount); };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(build, { timeout: 4000 });
      } else {
        setTimeout(build, 1400 + i * 400);
      }
    });
  }
  function schedulePrefetch() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchSearch, { timeout: 3000 });
    } else {
      setTimeout(prefetchSearch, 1200);
    }
  }
  if (document.readyState === 'complete') {
    schedulePrefetch();
  } else {
    window.addEventListener('load', schedulePrefetch, { once: true });
  }
})();
